/**
 * Die Rechenteile eines Diagramms — ohne SVG, ohne React.
 *
 * WARUM SIE HIER STEHEN UND NICHT IN DER KOMPONENTE. Drei Dinge an einem
 * Diagramm sind Rechnung und nicht Darstellung: welche Achsenmarken vernünftig
 * liegen, welcher Wert an einer Stelle des Zyklus abzulesen ist, und wo eine
 * Kurve ihre Grenze verlässt. Alle drei sind an einem Bild nur schwer zu
 * prüfen — man sieht einer Marke nicht an, ob sie bei 2,5 oder bei 2,4999
 * steht, und einer roten Fläche nicht, ob sie eine Stützstelle zu früh
 * beginnt. Als Funktionen sind sie in einer Zeile geprüft.
 *
 * Sie sind rein: gleiche Eingaben, gleiche Ausgaben. Keine Zeit, kein Zufall,
 * kein DOM.
 */

export type Track = Float32Array | Float64Array | number[]

/**
 * Der Wert an einer Stelle des Zyklus, 0 … 1.
 *
 * LINEAR ZWISCHEN DEN STÜTZSTELLEN und nicht die nächstgelegene: die
 * Abspielmarke läuft stetig, und ein Punkt, der von Stützstelle zu Stützstelle
 * springt, sieht bei 120 Werten je Zyklus aus wie ein Wackeln der Kurve.
 *
 * Die Stelle wird auf `t · (n − 1)` abgebildet — genau so, wie die Kurve
 * gezeichnet wird. Eine Reihe ohne Schlusspunkt wird dabei über die volle
 * Breite gestreckt statt umzulaufen; das ist eine Aussage über die Darstellung,
 * und der abgelesene Punkt muss ihr folgen, sonst läge er neben seiner eigenen
 * Kurve.
 */
export function sampleTrack(values: Track, t: number): number {
  const n = values.length
  if (n === 0) return NaN
  if (n === 1) return values[0]
  const x = Math.min(n - 1, Math.max(0, t * (n - 1)))
  const i = Math.floor(x)
  if (i >= n - 1) return values[n - 1]
  const f = x - i
  const a = values[i]
  const b = values[i + 1]
  if (!Number.isFinite(a) || !Number.isFinite(b)) return Number.isFinite(a) ? a : b
  return a + (b - a) * f
}

/**
 * Zusammenhängende Abschnitte, in denen die Reihe ihre Grenze verlässt —
 * als Anteil 0 … 1 der Breite.
 *
 * ZUSAMMENGEFASST UND NICHT JE STÜTZSTELLE. Eine Überschreitung dauert
 * typischerweise ein Zehntel des Zyklus, also ein Dutzend Stützstellen. Ein
 * Rechteck je Stützstelle ergäbe ein Dutzend aneinanderstossender Flächen mit
 * je einer Kante — und deren Kanten summieren sich in der Halbtransparenz zu
 * einem Streifenmuster, das aussieht wie Struktur, wo keine ist.
 */
export function overruns(values: Track, lo: number, hi: number): { from: number; to: number }[] {
  const n = values.length
  const out: { from: number; to: number }[] = []
  if (n < 2) return out
  const span = n - 1
  let start = -1
  for (let i = 0; i < n; i++) {
    const v = values[i]
    const bad = Number.isFinite(v) && (v < lo || v > hi)
    if (bad && start < 0) start = i
    if (!bad && start >= 0) {
      out.push({ from: start / span, to: (i - 1) / span })
      start = -1
    }
  }
  if (start >= 0) out.push({ from: start / span, to: 1 })
  return out
}

/**
 * Achsenmarken in runden Schritten über einen Wertebereich.
 *
 * Die Schrittweite kommt aus der Zehnerpotenz unter dem Viertel der Spanne und
 * wird auf 1, 2, 2,5 oder 5 davon aufgerundet — die vier Schritte, die ein
 * Leser ohne Nachrechnen versteht. Alles andere ergibt Marken wie 3,7 und 7,4,
 * bei denen man die Skala liest statt der Kurve.
 */
export function axisTicks(lo: number, hi: number, count = 4): number[] {
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return []
  const raw = (hi - lo) / count
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)))
  const step =
    [1, 2, 2.5, 5, 10].map((f) => f * magnitude).find((f) => f >= raw) ?? magnitude * 10
  const out: number[] = []
  /* Der erste Schritt wird gerundet angesetzt: `Math.ceil(lo / step)` allein
     erzeugt bei -0 den Wert -0, und der druckt sich als „-0". */
  for (let v = Math.ceil(lo / step) * step; v <= hi + step * 1e-9; v += step) {
    out.push(v === 0 ? 0 : v)
  }
  return out
}
